import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { CssBaseline } from '@mui/material';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { AccessibilityProvider } from './contexts/AccessibilityContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { Layout } from './components/Layout/Layout';
import Home from './pages/Home';
import CompanyRegister from './pages/CompanyRegister';
import SupportPage from './pages/SupportPage';
import PartnerPage from './pages/PartnerPage';
import ProductPage from './pages/ProductPage';
import SupportCenter from './pages/SupportCenter';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { Detections } from './pages/Detections';
import { Cases } from './pages/Cases';
import { Indicators } from './pages/Indicators';
import { Entities } from './pages/Entities';
import Settings from './pages/Settings';
import Profile from './pages/Profile';
import UserSettings from './pages/UserSettings';
import SetupPassword from './pages/SetupPassword';
import Integrations from './pages/Integrations';
import IndicatorDetailPage from './pages/IndicatorDetailPage';
import EntityDetailPage from './pages/EntityDetailPage';
import Billing from './pages/Billing';
import Reports from './pages/Reports';
import AdminLogin from './pages/admin/AdminLogin';
import AdminLayout from './components/Admin/AdminLayout';
import AdminOverview from './pages/admin/AdminOverview';
import AdminCustomers from './pages/admin/AdminCustomers';
import AdminCustomerDetail from './pages/admin/AdminCustomerDetail';
import AdminSystemSettings from './pages/admin/AdminSystemSettings';
import AdminAudit from './pages/admin/AdminAudit';
import AdminSupport from './pages/admin/AdminSupport';
import AdminSupportTicket from './pages/admin/AdminSupportTicket';
import DetectionDetailPage from './pages/DetectionDetailPage';
import CaseDetailPage from './pages/CaseDetailPage';
import { ProtectedRoute, RequirePlatformAdmin } from './components/Auth/ProtectedRoute';

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <AccessibilityProvider>
          <CssBaseline />
          <Router>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<Home />} />
          <Route path="/register" element={<CompanyRegister />} />
          <Route path="/login" element={<Login />} />
          <Route path="/product" element={<ProductPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/partner" element={<PartnerPage />} />
          <Route path="/setup-password" element={<SetupPassword />} />

          {/* Protected routes */}
          <Route
            path="/dashboard/*"
            element={
              <ProtectedRoute>
                <SettingsProvider>
                  <Layout>
                    <Routes>
                      <Route path="/" element={<Dashboard />} />
                      <Route path="/detections" element={<Detections />} />
                      <Route path="/detections/:id" element={<DetectionDetailPage />} />
                      <Route path="/cases" element={<Cases />} />
                      <Route path="/cases/:id" element={<CaseDetailPage />} />
                      <Route path="/indicators" element={<Indicators />} />
                      <Route path="/indicators/:id" element={<IndicatorDetailPage />} />
                      <Route path="/entities" element={<Entities />} />
                      <Route path="/entities/:id" element={<EntityDetailPage />} />
                      <Route path="/integrations" element={<Integrations />} />
                      <Route path="/defender-agents" element={<Navigate to="/dashboard/integrations" replace />} />
                      <Route path="/billing" element={<Billing />} />
                      <Route path="/reports" element={<Reports />} />
                      <Route path="/admin/customers" element={<Navigate to="/admin/customers" replace />} />
                      <Route path="/settings" element={<Settings />} />
                      <Route path="/profile" element={<Profile />} />
                      <Route path="/user-settings" element={<UserSettings />} />
                      <Route path="/support" element={<SupportCenter />} />
                    </Routes>
                  </Layout>
                </SettingsProvider>
              </ProtectedRoute>
            }
          />

          {/* Platform administration: separate area for Excubia staff */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route
            path="/admin/*"
            element={
              <RequirePlatformAdmin loginPath="/admin/login">
                <AdminLayout>
                  <Routes>
                    <Route path="/" element={<AdminOverview />} />
                    <Route path="/customers" element={<AdminCustomers />} />
                    <Route path="/customers/:id" element={<AdminCustomerDetail />} />
                    <Route path="/system-settings" element={<AdminSystemSettings />} />
                    <Route path="/audit" element={<AdminAudit />} />
                    <Route path="/support" element={<AdminSupport />} />
                    <Route path="/support/:id" element={<AdminSupportTicket />} />
                    <Route path="*" element={<Navigate to="/admin" replace />} />
                  </Routes>
                </AdminLayout>
              </RequirePlatformAdmin>
            }
          />

          {/* Redirect unknown routes to home for unauthenticated users */}
          <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Router>
        </AccessibilityProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
